import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const Header = ({ theme, toggleTheme, changeLanguage }) => {
  const { t, i18n } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const languages = [
    { code: 'en', name: '🇺🇸 English' },
    { code: 'pt', name: '🇧🇷 Português' },
    { code: 'es', name: '🇪🇸 Español' }
  ];

  const scrollToSection = (sectionId) => {
    // Fechar o menu mobile quando uma seção for selecionada
    setShowMobileMenu(false);
    
    // build path preserving locale prefix when present
    const parts = location.pathname.split('/').filter(Boolean);
    const first = parts[0];
    const supported = ['en', 'pt', 'es'];
    let base = '/';
    if (supported.includes(first)) {
      base = `/${first}`;
    }

    // If we're not on home (considering locale prefix), navigate to home anchor with prefix
    if (location.pathname !== base && location.pathname !== `${base}/`) {
      navigate(`${base}/#${sectionId}`);
      return;
    }
    
    // Se já estamos na Home, faça scroll suave
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  return (
    <header className="header">
      <div className="container">
        <nav className="nav">
          <img 
            src="/images/logo.png" 
            alt="Jobya" 
            className="logo" 
            onClick={() => {
              // preserve locale when navigating home
              const parts = location.pathname.split('/').filter(Boolean);
              const first = parts[0];
              const supported = ['en', 'pt', 'es'];
              if (supported.includes(first)) navigate(`/${first}`);
              else navigate('/');
            }}
            style={{ cursor: 'pointer' }}
          />
          
          {/* Menu desktop */}
          <ul className="nav-links desktop-nav">
            <li><a href="/#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>{t('nav.home')}</a></li>
            <li><a href="/#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>{t('nav.features')}</a></li>
            <li><a href="/#screenshots" onClick={(e) => { e.preventDefault(); scrollToSection('screenshots'); }}>{t('nav.screenshots')}</a></li>
            <li><a href="/#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>{t('nav.contact')}</a></li>
          </ul>

          {/* Controles desktop */}
          <div className="controls desktop-controls">
            <div className="language-selector">
              <button 
                className="language-btn"
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                  {languages.filter(lang => lang.code === i18n.language).map(lang => lang.name)} ▼
              </button>
              {showLanguageDropdown && (
                <div className="language-dropdown">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        // call the prop changeLanguage which will update i18n and URL
                        changeLanguage(lang.code);
                        setShowLanguageDropdown(false);
                      }}
                      style={{ 
                        fontWeight: i18n.language === lang.code ? 'bold' : 'normal' 
                      }}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button className="theme-toggle" onClick={toggleTheme}>
              {t('theme.theme')} {theme === 'light' ? t('theme.light') : t('theme.dark')}
            </button>
          </div>

          {/* Botão hambúrguer para mobile */}
          <button 
            className="hamburger mobile-only" 
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Menu mobile */}
          <div className={`mobile-menu ${showMobileMenu ? 'mobile-menu-open' : ''}`}>
            <ul className="mobile-nav-links">
              <li><a href="/#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>{t('nav.home')}</a></li>
              <li><a href="/#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>{t('nav.features')}</a></li>
              <li><a href="/#screenshots" onClick={(e) => { e.preventDefault(); scrollToSection('screenshots'); }}>{t('nav.screenshots')}</a></li>
              <li><a href="/#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>{t('nav.contact')}</a></li>
            </ul>
            
            <div className="mobile-controls">
              <div className="mobile-language-selector">
                <span className="mobile-control-label">{t('language.language')}:</span>
                <div className="mobile-language-buttons">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setShowMobileMenu(false);
                      }}
                      className={`mobile-language-btn ${i18n.language === lang.code ? 'active' : ''}`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mobile-theme-selector">
                <span className="mobile-control-label">{t('theme.theme')}:</span>
                <button className="mobile-theme-toggle" onClick={toggleTheme}>
                  {theme === 'light' ? t('theme.dark') : t('theme.light')}
                </button>
              </div>
            </div>
          </div>

          {/* Overlay para fechar menu mobile */}
          {showMobileMenu && (
            <div 
              className="mobile-menu-overlay"
              onClick={() => setShowMobileMenu(false)}
            />
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
